import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Navigation } from 'lucide-react';
import { GpxRoute, RouteRegion } from '../../../types';

type AdminRoutesPanelProps = {
  groupedRoutes: Record<string, GpxRoute[]>;
  expandedRegions: Set<RouteRegion>;
  onToggleRegion: (region: RouteRegion) => void;
  renderRouteCard: (route: GpxRoute) => React.ReactNode;
};

const AdminRoutesPanel: React.FC<AdminRoutesPanelProps> = ({
  groupedRoutes,
  expandedRegions,
  onToggleRegion,
  renderRouteCard
}) => {
  const { t } = useTranslation();
  if (Object.keys(groupedRoutes).length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4 text-center">
        <Navigation className="text-blue-500/20" size={80} />
        <p className="text-sm font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('admin.no_routes_found', 'Ingen ruter funnet')}</p>
      </div>
    );
  }

  return (
    <>
      {(Object.keys(groupedRoutes) as RouteRegion[]).map((region) => {
        const regionRoutes = groupedRoutes[region] || [];
        if (regionRoutes.length === 0) return null;
        const isExpanded = expandedRegions.has(region);

        return (
          <div key={region} className="bg-[var(--bg-surface)]/50 rounded-2xl overflow-hidden border border-[var(--border-primary)] mb-4">
            <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-[var(--bg-surface-alt)] transition-colors" onClick={() => onToggleRegion(region)}>
              <span className="text-2xs font-black uppercase tracking-widest text-[var(--text-secondary)]">
                {t(`admin.regions.${region.toLowerCase()}`, region)} ({regionRoutes.length})
              </span>
              <ChevronDown size={16} className={`text-[var(--text-muted)] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </div>
            {isExpanded && (
              <div className="p-4 pt-0 space-y-4">
                {regionRoutes.map((route) => renderRouteCard(route))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export default AdminRoutesPanel;
